import { useEffect, useRef, useState, type ReactNode } from 'react'
import { DayPicker, type DateRange as PickedRange } from 'react-day-picker'
import { ptBR } from 'date-fns/locale/pt-BR'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from './Button.tsx'
import { Icon } from './Icon.tsx'
import { icons } from './icons.ts'
import { FOCUS_RING } from './focusRing.ts'

/**
 * A window of days, chosen on a calendar.
 *
 * Two bare `<input type="date">` beside a `<select>` asked somebody to type a
 * range one edge at a time in the browser's own widget, which looked like
 * nothing else on the toolbar. This is one chip, in the filters' own
 * clothes, opening a calendar where a range is two clicks and the usual
 * answers -- hoje, esta semana -- are one.
 *
 * The calendar is `react-day-picker`, unstyled, dressed in the product's
 * tokens through its class names. Days are the pharmacy's days: the values
 * cross the wire as `YYYY-MM-DD`, never as instants, so "hoje" means today
 * on the counter and not in UTC.
 */
export interface DateWindow {
  /** `YYYY-MM-DD`, or empty for an open edge. */
  readonly from: string
  readonly to: string
}

export function DateRange({
  label,
  value,
  onChange,
  header,
  presets = PAST_PRESETS,
  minDate,
  clearValue,
}: {
  /** What the chip says the window is on: "Entrega", "Criada", "Período". */
  label: string
  value: DateWindow
  onChange: (next: DateWindow) => void
  /**
   * Anything the caller wants above the calendar -- the queue puts a pair of
   * tabs there to say which date the window is on. The picker itself knows
   * nothing about that: it is a window of days, wherever it is used.
   */
  header?: ReactNode
  /** The one-click windows offered. Backwards by default. */
  presets?: readonly DatePreset[]
  /** Earliest selectable day, when this particular report has a product cutoff. */
  minDate?: Date
  /** What clearing means when the period has a required lower bound. */
  clearValue?: DateWindow
}) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const [at, setAt] = useState({ left: 0, top: 0, maxWidth: 320 })

  const show = () => {
    const box = anchor.current?.getBoundingClientRect()
    const sheet = panel.current
    if (box === undefined || sheet === null) return
    // Shown before measuring: a popover has no size until it is in the top
    // layer, and reading its height first gives zero.
    sheet.showPopover()
    const own = sheet.getBoundingClientRect()
    const width = document.documentElement.clientWidth
    const height = document.documentElement.clientHeight
    setAt({
      left: Math.max(8, Math.min(box.left, width - own.width - 8)),
      top:
        box.bottom + own.height + 8 > height
          ? Math.max(8, box.top - own.height - 4)
          : box.bottom + 4,
      maxWidth: Math.max(1, width - 16),
    })
    setOpen(true)
  }

  const hide = () => {
    const sheet = panel.current
    if (sheet !== null && sheet.matches(':popover-open')) sheet.hidePopover()
    setOpen(false)
  }

  useEffect(() => {
    if (!open) return undefined
    const dismiss = (event: MouseEvent) => {
      const target = event.target as Node
      if (panel.current?.contains(target) === true) return
      if (anchor.current?.contains(target) === true) return
      hide()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide()
    }
    // Deferred, or the click that opened it closes it in the same tick.
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', dismiss)
      document.addEventListener('keydown', onKey)
    }, 0)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', dismiss)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const selected: PickedRange | undefined =
    value.from === '' && value.to === ''
      ? undefined
      : { from: parseDay(value.from), to: parseDay(value.to) }

  const pick = (range: PickedRange | undefined) => {
    if (range === undefined && clearValue !== undefined) {
      onChange(clearValue)
      return
    }
    const from = range?.from
    const boundedFrom =
      from !== undefined && minDate !== undefined && from < startOfDay(minDate) ? minDate : from
    onChange({ from: formatDay(boundedFrom), to: formatDay(range?.to) })
  }

  const summary =
    value.from === '' && value.to === ''
      ? 'Todas'
      : value.from !== '' && value.to !== '' && value.from === value.to
        ? short(value.from)
        : `${value.from === '' ? '…' : short(value.from)} – ${value.to === '' ? '…' : short(value.to)}`

  return (
    <div ref={anchor} className="relative inline-flex">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`border-stroke-weak bg-fill-white text-tSmall text-text-strong flex h-8 items-center gap-2 rounded-[var(--radius-base)] border px-3 ${FOCUS_RING}`}
        onClick={() => (open ? hide() : show())}
      >
        <span className="text-text-weak">{label}:</span>
        <span className="max-w-[220px] truncate tabular-nums">{summary}</span>
        <Icon name="expand" className="text-icon-neutral text-[10px]" />
      </button>

      {/* No `display` utility on this element, ever: a closed popover is
          hidden by a user-agent rule, and an author class beats it. */}
      <div
        ref={panel}
        popover="manual"
        role="dialog"
        aria-label="Período"
        className="border-stroke-weak bg-background-raised m-0 max-h-[calc(100vh-1rem)] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-[8px] border p-3 shadow-lg"
        style={{
          position: 'fixed',
          inset: 'auto',
          left: at.left,
          top: at.top,
          maxWidth: at.maxWidth,
        }}
      >
        <div className="flex flex-col gap-4 sm:flex-row">
          {/* The usual answers, one click each. */}
          <div className="border-stroke-weak flex w-full shrink-0 flex-col gap-0.5 border-b pb-3 sm:w-36 sm:border-r sm:border-b-0 sm:pr-3 sm:pb-0">
            <span className="text-tTiny text-text-weak mb-1 px-2 uppercase tracking-[0.04em]">
              Atalhos
            </span>
            {presets.map((preset) => {
              const range = preset.range(new Date())
              const disabled =
                minDate !== undefined && (range.to === undefined || range.to < startOfDay(minDate))
              return (
                <button
                  key={preset.label}
                  type="button"
                  disabled={disabled}
                  className={`text-tSmall text-text-strong hover:bg-fill-weak/60 rounded-[4px] px-2 py-1 text-left disabled:text-text-disabled disabled:cursor-default ${FOCUS_RING}`}
                  onClick={() => pick(range)}
                >
                  {preset.label}
                </button>
              )
            })}
            <Button
              size="small"
              variant="tertiary"
              tone="neutral"
              className="mt-2 self-start"
              onClick={() => pick(undefined)}
            >
              Limpar
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            {header}

            <DayPicker
              mode="range"
              locale={ptBR}
              selected={selected}
              onSelect={pick}
              disabled={minDate === undefined ? undefined : { before: startOfDay(minDate) }}
              showOutsideDays
              components={{
                Chevron: ({ orientation }) => (
                  <FontAwesomeIcon
                    icon={orientation === 'left' ? icons.back : icons.forward}
                    className="text-[11px]"
                  />
                ),
              }}
              classNames={{
                root: 'text-tSmall text-text-strong select-none',
                months: 'relative',
                month: 'flex flex-col gap-2',
                month_caption: 'flex h-7 items-center justify-center',
                caption_label: 'text-tPSemi text-text-strong capitalize',
                nav: 'absolute inset-x-0 top-0 flex h-7 items-center justify-between',
                button_previous: `text-icon-neutral hover:bg-fill-weak flex size-7 items-center justify-center rounded-[4px] ${FOCUS_RING}`,
                button_next: `text-icon-neutral hover:bg-fill-weak flex size-7 items-center justify-center rounded-[4px] ${FOCUS_RING}`,
                month_grid: 'border-collapse',
                weekdays: '',
                weekday: 'text-tTiny text-text-weak h-7 w-8 font-normal uppercase',
                week: '',
                day: 'p-0 text-center',
                day_button: `size-8 rounded-[4px] tabular-nums hover:bg-fill-weak ${FOCUS_RING}`,
                today: 'font-semibold text-text-brand',
                outside: 'text-text-disabled',
                selected: '',
                range_start:
                  '[&>button]:bg-fill-brand [&>button]:text-white [&>button]:rounded-r-none',
                range_end:
                  '[&>button]:bg-fill-brand [&>button]:text-white [&>button]:rounded-l-none',
                range_middle:
                  '[&>button]:bg-fill-brand-weak [&>button]:text-text-strong [&>button]:rounded-none',
                disabled: 'text-text-disabled',
                hidden: 'invisible',
                focused: '',
                chevron: '',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

/** One click, one window. */
export interface DatePreset {
  readonly label: string
  readonly range: (now: Date) => PickedRange
}

/**
 * Two sets, because a date is asked two ways. "Criada" is about what came
 * in: today, yesterday, the last week. "Entrega" is a promise: today,
 * tomorrow, the coming week. The caller says which; a preset for a window
 * of rows that do not exist yet is noise on the other.
 */
export const PAST_PRESETS: readonly DatePreset[] = [
  { label: 'Hoje', range: (now) => ({ from: now, to: now }) },
  { label: 'Ontem', range: (now) => ({ from: addDays(now, -1), to: addDays(now, -1) }) },
  { label: 'Últimos 7 dias', range: (now) => ({ from: addDays(now, -6), to: now }) },
  { label: 'Esta semana', range: (now) => ({ from: addDays(now, -now.getDay()), to: now }) },
  {
    label: 'Este mês',
    range: (now) => ({ from: new Date(now.getFullYear(), now.getMonth(), 1), to: now }),
  },
]

export const FUTURE_PRESETS: readonly DatePreset[] = [
  { label: 'Hoje', range: (now) => ({ from: now, to: now }) },
  { label: 'Amanhã', range: (now) => ({ from: addDays(now, 1), to: addDays(now, 1) }) },
  { label: 'Próximos 7 dias', range: (now) => ({ from: now, to: addDays(now, 6) }) },
  { label: 'Esta semana', range: (now) => ({ from: now, to: addDays(now, 6 - now.getDay()) }) },
  {
    label: 'Este mês',
    range: (now) => ({ from: now, to: new Date(now.getFullYear(), now.getMonth() + 1, 0) }),
  },
]

function addDays(day: Date, n: number): Date {
  const next = new Date(day)
  next.setDate(next.getDate() + n)
  return next
}

function startOfDay(day: Date): Date {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate())
}

/** `YYYY-MM-DD` in local time -- the counter's day, not UTC's. */
function formatDay(day: Date | undefined): string {
  if (day === undefined) return ''
  const y = day.getFullYear()
  const m = String(day.getMonth() + 1).padStart(2, '0')
  const d = String(day.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function parseDay(text: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text)
  if (match === null) return undefined
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

/** `05/09`, for the chip. */
function short(day: string): string {
  const parsed = parseDay(day)
  return parsed === undefined
    ? day
    : `${String(parsed.getDate()).padStart(2, '0')}/${String(parsed.getMonth() + 1).padStart(2, '0')}`
}
